import styled from 'styled-components';

export const BadgeContainer = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${(props) => (props.hasIcon ? '6px' : '8px')};
  padding: ${(props) => {
    if (props.size === 'small') return '2px 8px';
    if (props.size === 'large') return '8px 16px';
    return props.shape === 'pill' ? '8px 14px' : '4px 12px';
  }};
  border-radius: ${(props) => {
    if (props.shape === 'pill') return '20px';
    if (props.shape === 'square') return '4px';
    return '6px';
  }};
  font-size: ${(props) => {
    if (props.size === 'small') return '11px';
    if (props.size === 'large') return '14px';
    return '12px';
  }};
  font-weight: 600;
  letter-spacing: ${(props) =>
    props.uppercase ? '0.5px' : props.shape === 'pill' ? '0' : '0.02em'};
  text-transform: ${(props) => (props.uppercase ? 'uppercase' : 'none')};
  white-space: nowrap;
  transition: all 0.2s ease;
  cursor: ${(props) => (props.interactive ? 'pointer' : 'default')};

  /* Icon styling */
  .badge-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 0;

    svg {
      display: block;
      width: ${(props) => {
        if (props.size === 'small') return '14px';
        if (props.size === 'large') return '18px';
        return '16px';
      }};
      height: ${(props) => {
        if (props.size === 'small') return '14px';
        if (props.size === 'large') return '18px';
        return '16px';
      }};
    }
  }

  /* Text styling */
  .badge-text {
    display: inline-flex;
    align-items: center;
    line-height: 1;
  }

  /* Background and color based on variant or custom color */
  ${(props) => {
    // If custom color is provided
    if (props.color) {
      return `
        background: ${
          props.outline
            ? props.theme.name === 'dark'
              ? `${props.color}33`
              : `${props.color}1A`
            : props.theme.name === 'dark'
            ? `${props.color}33`
            : `${props.color}1A`
        };
        color: ${props.color};
        border: 1px solid ${props.outline ? `${props.color}4D` : 'transparent'};
      `;
    }

    // If outline style
    if (props.outline) {
      return `
        background: ${
          props.theme.name === 'dark'
            ? 'rgba(250, 70, 22, 0.1)'
            : 'rgba(250, 70, 22, 0.05)'
        };
        color: ${props.theme?.colors?.primary || '#FA4616'};
        border: 1px solid rgba(250, 70, 22, 0.3);
      `;
    }

    // Variant-based colors
    switch (props.variant) {
      case 'success':
        return `
          background: ${
            props.theme.name === 'dark'
              ? props.theme?.colors?.success
                ? `${props.theme.colors.success}33`
                : 'rgba(34, 197, 94, 0.2)'
              : props.theme?.colors?.success
              ? `${props.theme.colors.success}1A`
              : 'rgba(34, 197, 94, 0.1)'
          };
          color: ${props.theme?.colors?.success || '#22C55E'};
          border: 1px solid transparent;
        `;
      case 'primary':
      case 'info':
        return `
          background: ${
            props.theme.name === 'dark'
              ? 'rgba(59, 130, 246, 0.2)'
              : 'rgba(59, 130, 246, 0.1)'
          };
          color: #3B82F6;
          border: 1px solid transparent;
        `;
      case 'warning':
      case 'error':
        return `
          background: ${
            props.theme.name === 'dark'
              ? 'rgba(220, 38, 38, 0.2)'
              : 'rgba(220, 38, 38, 0.1)'
          };
          color: #DC2626;
          border: 1px solid transparent;
        `;
      case 'alert':
        return `
          background: ${
            props.theme.name === 'dark'
              ? 'rgba(245, 158, 11, 0.2)'
              : 'rgba(245, 158, 11, 0.1)'
          };
          color: #F59E0B;
          border: 1px solid transparent;
        `;
      case 'premium':
        return `
          background: ${
            props.theme.name === 'dark'
              ? 'rgba(139, 92, 246, 0.2)'
              : 'rgba(139, 92, 246, 0.1)'
          };
          color: #8B5CF6;
          border: 1px solid transparent;
        `;
      case 'new':
        return `
          background: ${
            props.theme.name === 'dark'
              ? 'rgba(6, 182, 212, 0.2)'
              : 'rgba(6, 182, 212, 0.1)'
          };
          color: #06B6D4;
          border: 1px solid transparent;
        `;
      case 'accent':
        return `
          background: ${
            props.theme.name === 'dark'
              ? 'rgba(250, 70, 22, 0.15)'
              : 'rgba(250, 70, 22, 0.08)'
          };
          color: ${props.theme?.colors?.primary || '#FA4616'};
          border: 1px solid rgba(250, 70, 22, 0.3);
        `;
      case 'neutral':
      case 'location':
        return `
          background: ${
            props.theme.name === 'dark'
              ? 'rgba(148, 163, 184, 0.15)'
              : 'rgba(100, 116, 139, 0.08)'
          };
          color: ${props.theme.name === 'dark' ? '#94A3B8' : '#64748B'};
          border: 1px solid ${
            props.theme.name === 'dark'
              ? 'rgba(148, 163, 184, 0.2)'
              : 'rgba(100, 116, 139, 0.12)'
          };
        `;
      case 'dark':
        return `
          background: #475569;
          color: #ffffff;
          border: 1px solid transparent;
        `;
      case 'occasional':
        return `
          background: ${
            props.theme.name === 'dark'
              ? 'rgba(167, 139, 250, 0.2)'
              : 'rgba(167, 139, 250, 0.1)'
          };
          color: #A78BFA;
          border: 1px solid transparent;
        `;
      default:
        // Default gray badge (for shape="pill" by default)
        if (props.shape === 'pill') {
          return `
            background: ${
              props.theme.name === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(255, 255, 255, 1)'
            };
            border: 1px solid ${
              props.theme.name === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.08)'
            };
            color: ${props.theme.name === 'dark' ? '#e2e8f0' : '#334155'};
          `;
        }
        return `
          background: ${
            props.theme.name === 'dark'
              ? 'rgba(148, 163, 184, 0.2)'
              : 'rgba(148, 163, 184, 0.1)'
          };
          color: #94A3B8;
          border: 1px solid transparent;
        `;
    }
  }}

  /* Interactive hover effects */
  ${(props) =>
    props.interactive &&
    `
    &:hover {
      background: ${
        props.theme.name === 'dark'
          ? 'rgba(255, 255, 255, 0.08)'
          : 'rgba(250, 70, 22, 0.05)'
      };
      border-color: ${props.theme?.colors?.primary || '#fa4616'};
      transform: translateY(-2px);
    }
  `}
`;

export const BadgeCount = styled.span`
  background: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: ${(props) => (props.size === 'small' ? '10px' : '12px')};
  font-weight: 600;
  line-height: 1;
`;
